import { useMemo } from 'react';

import { datadogLogs } from '@datadog/browser-logs';
import { Box, Stack, Typography } from '@mui/material';

import { TeamRoles } from '../constants/TeamRoles';
import { Team } from '../store/sageHomeApi';
import { pluralize } from '../utils/intlUtils';
import { getTeamMemberDisplayName } from '../utils/nameUtils';
import TeamMemberAvatar from './TeamMemberAvatar';

interface TeamCardProps {
    team: Team;
}

function TeamCard({ team }: TeamCardProps) {
    const { careRecipient, communityCaregivers, licensedCaregivers } = useMemo(
        () =>
            team.members.reduce(
                (acc, member) => {
                    if (member.role === TeamRoles.CareRecipient) {
                        return { ...acc, careRecipient: member };
                    } else if (member.role === TeamRoles.CommunityCaregiver) {
                        return {
                            ...acc,
                            communityCaregivers: [...acc.communityCaregivers, member],
                        };
                    } else if (member.role === TeamRoles.LicensedCaregiver) {
                        return { ...acc, licensedCaregivers: [...acc.licensedCaregivers, member] };
                    } else {
                        datadogLogs.logger.warn(
                            `TeamCard: Unexpected team member role "${member.role}" encountered.`,
                            member
                        );
                        return acc;
                    }
                },
                {
                    careRecipient: undefined as Team['members'][0] | undefined,
                    communityCaregivers: [] as Team['members'],
                    licensedCaregivers: [] as Team['members'],
                }
            ),
        [team.members]
    );

    return (
        <Box
            sx={{
                width: '100%',
                display: 'grid',
                gridTemplateColumns: 'max-content 1fr',
                gridTemplateRows: 'repeat(2, 1fr)',
                columnGap: 2,
                rowGap: 1,
            }}
        >
            {careRecipient && (
                <>
                    <Box sx={{ gridRow: '1 / -1' }}>
                        <TeamMemberAvatar teamMember={careRecipient} />
                    </Box>
                    <Box sx={{ gridColumn: 2, alignSelf: 'flex-end' }}>
                        <Typography variant="body1" sx={{ fontWeight: 600, textAlign: 'left' }}>
                            {getTeamMemberDisplayName(careRecipient, { usePreferred: true })}
                        </Typography>
                    </Box>
                </>
            )}

            <Stack
                direction="row"
                sx={{
                    gridColumn: 2,
                    gridRow: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    alignSelf: 'flex-start',
                }}
            >
                {pluralize(
                    communityCaregivers.length + licensedCaregivers.length,
                    'Caregiver',
                    'Caregivers'
                )}
            </Stack>
        </Box>
    );
}

export default TeamCard;
